import { useField } from 'formik';
import React, { ChangeEvent, useCallback } from 'react';

import { InputAdornment, SvgIconProps,
  Autocomplete as MUIAutocomplete, AutocompleteProps as MUIAutocompleteProps,
  AutocompleteRenderInputParams, AutocompleteValue as MUIAutocompleteValue,
  AutocompleteRenderOptionState,
  MenuItem
} from '@mui/material';

import { BaseTextField, BaseTextFieldProps } from '../BaseTextField';
import { makeStyles } from 'tss-react/mui';

/*
  This one is weird!
  The point of this component is to extend the mui autocomplete component with our styling/defaults

  1. So we start by extending their props (which have been imported as MUIAutocompleteProps)
  2. These props are generic, so our extended interface has to be generic as well
  3. We'd like to supply a default `renderInput` prop, but it's required on their interface. We mark it option by
    first removing it from their interface (the Omit<> type), and adding it again in our additional properties
*/
export interface AutocompleteProps<
  T,
  TMultiple extends boolean | undefined = false,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = false,
  // TChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
> extends Omit<MUIAutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>, 'renderInput' | 'renderOption'> {
  name: string;
  label?: React.ReactNode;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  groupBy?: (option: any) => string;
  helperText?: React.ReactNode;
  autoFocus?: boolean;
  transformValue?: (value: T | null) => unknown;
  icon?: React.ComponentType<SvgIconProps>;
  autoFiller?: (value: string, options: readonly T[]) => { value: T | null; inputValue: string } | null;

  inputValue: string;
  setInputValue: (val: string) => void;
  value?: any;
  setValue?: (value?: any) => void;
  touched?: boolean;
  error?: string;

  renderOption?: (opt: T, state: AutocompleteRenderOptionState) => React.ReactNode;

  openWithNoTerm?: boolean;
  placeholder?: string;
  startAdornment?: React.ReactNode | ((value: T) => React.ReactNode);
  endAdornment?: React.ReactNode;

  inputClasses?: BaseTextFieldProps['classes'];
  InputProps?: BaseTextFieldProps['InputProps'];
}

const defaultTransformValue = function <T>(v: T) {
  return v;
};


type UseAutocompleteClassesProps = Partial<Pick<AutocompleteProps<unknown>, 'classes' | 'inputValue'>>;
export const useAutocompleteClasses = makeStyles<UseAutocompleteClassesProps>({ name: 'UpliftAutocomplete' })(() => ({
  // inputRoot: {
  //   '& .MuiAutocomplete-endAdornment': {
  //     right: theme.spacing(1),
  //   },
  // },
  // option: {},
  // popper: {},
  // paper: {},

  // clearIndicator: {
  //   visibility: props?.inputValue ? 'visible' : 'hidden',
  // },

  // listbox: {
  //   maxWidth: '100%',
  //   overflowX: 'hidden',
  // },

  // groupLabel: {
  //   color: theme.palette.grey[900],
  //   borderTop: '1px solid',
  //   borderTopColor: theme.palette.grey[200],
  //   textTransform: 'uppercase',
  //   fontSize: '12px',
  //   fontWeight: 700,

  //   'li:first-child &': {
  //     borderTop: 0,
  //   }
  // },
  endAdornment: {
    right: '8px',
  }
}));

export const AutocompleteRaw = <
  T extends { id: string },
  TMultiple extends boolean | undefined = false,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = false
>({
  name,
  helperText,
  label,
  transformValue = defaultTransformValue,
  icon: Icon,
  renderInput,

  setInputValue,
  value: _value,
  setValue,
  touched,
  error,

  openWithNoTerm,
  autoFiller,
  inputClasses,

  renderOption,

  ...props
}: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>) => {
  const { classes } = useAutocompleteClasses(props, { props });
  const [ isOpen, setIsOpen ] = React.useState<boolean>(false);
  const valueAsIs = transformValue === defaultTransformValue;
  const value = React.useMemo(() => {
    if (valueAsIs) {
      return _value;
    }
    return props.options.find(o => o.id === _value) ?? null;
  }, [ valueAsIs, props.options, _value ]);

  const handleValueChange = (e: React.ChangeEvent<unknown>, newValue: MUIAutocompleteValue<T, TMultiple, TDisableClearable, TFreeSolo> | null) => {
    // if (Array.isArray(newValue)) {
    //   return;
    // }

    // if (typeof newValue === 'string') {
    //   return;
    // }

    // TODO: remove `transformValue`, rewrite autocomplete to just connect with formik and without any custom logic inside
    // because it is very hard to support right now
    setValue?.(transformValue(newValue as any) || null);
    if (props.onChange) props.onChange(e as ChangeEvent<any>, newValue as any, 'selectOption');
  };

  const handleInputValueChange = (e: React.ChangeEvent<unknown>, inputValue: string) => {
    if (props.freeSolo) {
      setInputValue(inputValue);
      setValue?.(inputValue);
    } else {
      const filled = autoFiller?.(inputValue, props.options) || null;

      if (filled) {
        const { value: newValue, inputValue: newInputValue } = filled;

        setInputValue(newInputValue);

        // this prevents a race condition
        if (_value === newValue?.id) return;

        setValue?.(transformValue(newValue) || null);
        setIsOpen(false);
      } else {
        setInputValue(inputValue);
        if (!inputValue) setValue?.(null);
      }
    }
  };

  const handleOpenRequest: AutocompleteProps<T>['onOpen'] = (e) => {
    if (
      openWithNoTerm === false
      && e.type === 'mousedown'
      && !props.inputValue
    ) {
      // should not be opened if user not enter anything
      // but open if user enter anything -> blur -> focus again
      return;
    }
    if (e.type === 'change' && e.target !== document.activeElement) {
      return;
    }

    setIsOpen(true);
  };

  const handleClose: AutocompleteProps<T>['onClose'] = () => {
    setIsOpen(false);
  };

  const defaultRenderInput = useCallback(({ InputProps: { className: _className, ...InputProps }, ...params }: AutocompleteRenderInputParams) => {
    const getStartAdornment = () => {
      if (props.startAdornment) {
        if (typeof props.startAdornment === 'function') {
          return (
            <InputAdornment position="start">
              {props.startAdornment(value)}
            </InputAdornment>
          );
        }

        return (
          <InputAdornment position="start">
            {props.startAdornment}
          </InputAdornment>
        );
      } else if (!props.startAdornment && Icon) {
        return (
          <InputAdornment position="start">
            <Icon />
          </InputAdornment>
        );
      }

      return;
    };

    const getEndAdornment = () => {
      if (props.endAdornment) {
        return (
          <InputAdornment position="end" className={classes.endAdornment}>
            {props.endAdornment}
          </InputAdornment>
        );
      }

      return undefined;
    };

    const _error = Boolean(error && helperText && touched) || Boolean(touched && error);
    const _helperText = (touched && error) || helperText;

    return (
      <BaseTextField
        name={name}
        label={label}
        {...params}
        color="secondary"
        placeholder={props.placeholder}
        autoFocus={props.autoFocus}
        error={_error}
        helperText={_helperText}
        inputClasses={inputClasses}
        slotProps={{
          input: {
            ...props.InputProps,
            ...InputProps,
            startAdornment: getStartAdornment() ?? InputProps.startAdornment,
            endAdornment: getEndAdornment() ?? InputProps.endAdornment,
          }
        }}
        data-testid="autocomplete-input"
      />
    );
  }, [ error, helperText, touched, name, label, props, classes.endAdornment, inputClasses, Icon, value ]);

  return (
    <MUIAutocomplete
      {...props}
      clearOnBlur={false}
      classes={classes}
      renderInput={renderInput ?? defaultRenderInput}
      value={value}
      onChange={handleValueChange}
      inputValue={props.inputValue}
      onInputChange={handleInputValueChange}
      onOpen={handleOpenRequest}
      onClose={handleClose}
      open={isOpen}
      renderOption={renderOption ? (_p, opt, state) => <MenuItem {..._p} key={opt.id}>{renderOption?.(opt, state)}</MenuItem> : undefined}
      slotProps={{
        listbox: {
          sx: { maxHeight: '260px' }
        },
      }}
      onBlur={(event) => {
        setIsOpen(false);
        props?.onBlur?.(event);
      }}
    />
  );
};

export const Autocomplete = <
  T extends { id: string },
  TMultiple extends boolean | undefined = false,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = false
>(
  props: Omit<AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>, 'value' | 'touched' | 'error' | 'inputValue' | 'setInputValue'>
) => {
  const [ inputValue, setInputValue ] = React.useState<string>('');
  const [ field, meta, helpers ] = useField(props.name);

  React.useEffect(() => {
    if (!meta.touched && !field.value) {
      setInputValue('');
    }
  }, [ meta.touched, field.value ]);

  // hooks into the autocomplete's onChange incase a side effect is needed
  // ex: resetting another formik field before this ones value changes
  const handleSetValue = useCallback((value?: any) => {
    props.setValue?.(value);
    helpers.setValue(value);
  }, [ helpers, props ]);

  return (
    <AutocompleteRaw
      {...props}
      data-testid={`input-${props.name}`}
      inputValue={inputValue}
      setInputValue={setInputValue}
      value={field.value ?? null}
      setValue={handleSetValue}
      touched={meta.touched}
      error={meta.error}
      onBlur={() => {
        Promise.resolve().then(() => {
          helpers.setTouched(true, true);
        });
      }}
    />
  );
};
